import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #submenuMutationObserverTimer;

  /** @type {number | undefined} */
  #pointerReconcileFrame;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('keydown', this.#onKeyDown);

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    if (this.#pointerReconcileFrame !== undefined) {
      cancelAnimationFrame(this.#pointerReconcileFrame);
      this.#pointerReconcileFrame = undefined;
    }

    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;

    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupSubmenuObservers();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#updateActiveSubmenuHeight();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  /**
   * Close an expanded desktop menu with Escape and restore focus to its trigger.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const activeItem = this.#state.activeItem;
    event.preventDefault();
    this.#deactivate(activeItem, true);
    activeItem.focus();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    const activeItem = this.#state.activeItem;

    if (!activeItem) return;

    if (this.#pointerReconcileFrame !== undefined) {
      cancelAnimationFrame(this.#pointerReconcileFrame);
    }

    this.#pointerReconcileFrame = requestAnimationFrame(() => {
      this.#pointerReconcileFrame = undefined;

      if (this.#state.activeItem !== activeItem) return;

      const target = document.elementFromPoint(x, y);
      if (!target) return;

      const listItem = target.closest('.menu-list__list-item');

      if (listItem && !listItem.contains(activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');

    const boundary = isOverlap
      ? this.headerComponent?.querySelector('.header__row--top')
      : this.headerComponent;

    item.style.setProperty(
      '--box-height',
      `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;

    if (this.#pointerReconcileFrame !== undefined) {
      cancelAnimationFrame(this.#pointerReconcileFrame);
      this.#pointerReconcileFrame = undefined;
    }

    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    this.#cancelScheduledDeactivate();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    const isDefaultSlot = event.target.slot === '';
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    // Plain links must remain native links. Hovering or focusing them only
    // closes a previously open submenu and never creates expanded state.
    if (!hasSubmenu && isDefaultSlot) {
      this.#deactivate(this.#state.activeItem, true);
      return;
    }

    if (item === this.#state.activeItem) return;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    item.ariaExpanded = 'true';

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      this.#observeSubmenuSize(submenu, item);

      // Monitor DOM mutations to catch deferred content injection
      // from section hydration
      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#queueSubmenuHeightUpdate(submenu, item);
      });

      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      // Auto-disconnect after 500ms to prevent memory leaks
      this.#submenuMutationObserverTimer = setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = getSubmenuHeight(submenu);

    // For overflow menu, the height needs to be either content of
    // the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        /*
         * When the submenu is inside the overflow menu, its offsetHeight
         * is not valid due to the lack of padding. Measuring the
         * overflowMenu.offsetHeight is easier.
         */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item when the pointer/focus actually
   * leaves the current menu and its submenu.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const menu = findSubmenu(activeItem);

    const relatedTarget =
      event.relatedTarget instanceof Node
        ? event.relatedTarget
        : null;

    /*
     * Some browsers can emit pointerleave when hit-testing changes
     * between the <li> and one of its own descendants.
     */
    const isMovingWithinItem =
      relatedTarget !== null &&
      event.target.contains(relatedTarget);

    /*
     * Keep the current top-level item active when moving directly
     * into its submenu.
     */
    const isMovingToSubmenu =
      relatedTarget !== null &&
      Boolean(menu?.contains(relatedTarget));

    /*
     * Preserve the overflow menu behaviour.
     */
    const isMovingToOverflowMenu =
      relatedTarget instanceof Element &&
      (Boolean(relatedTarget.closest('[slot="overflow"]')) || Boolean(this.overflowMenu?.contains(relatedTarget)));

    /*
     * Preserve keyboard/focus behaviour.
     */
    const isFocusStillWithinMenu =
      Boolean(menu?.contains(document.activeElement));

    if (
      isMovingWithinItem ||
      isMovingToSubmenu ||
      isMovingToOverflowMenu ||
      isFocusStillWithinMenu
    ) {
      return;
    }

    this.#scheduleDeactivate();
  }

  #cancelScheduledDeactivate() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  #scheduleDeactivate() {
    this.#cancelScheduledDeactivate();

    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = undefined;
      this.#deactivate();
    }, 220);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    if (force) {
      this.#cancelScheduledDeactivate();
    }

    const submenu = findSubmenu(item);

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (submenu?.matches(':hover') || this.overflowListHovered || this.overflowMenu?.matches(':hover'))) {
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);
    this.#cleanupSubmenuObservers();

    this.#state.activeItem = null;
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu?.querySelector('slot')?.assignedElements();

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );

        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  /**
   * Calculate and set the full open header height.
   * If the submenu is not open, the full open header height is 0.
   *
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const headerVisibleHeight =
      isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector('.header__row--top')
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');

    images?.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  /**
   * Watch the active submenu and its measured child for image/layout changes.
   *
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   */
  #observeSubmenuSize(submenu, item) {
    this.#cleanupSubmenuObservers();

    this.#submenuResizeObserver = new ResizeObserver(() => {
      this.#queueSubmenuHeightUpdate(submenu, item);
    });

    this.#submenuResizeObserver.observe(submenu);

    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
    if (submenuInner instanceof HTMLElement) {
      this.#submenuResizeObserver.observe(submenuInner);
    }
  }

  /**
   * Update the active submenu height after layout has settled.
   *
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   */
  #queueSubmenuHeightUpdate(submenu, item) {
    requestAnimationFrame(() => {
      // Double requestAnimationFrame to ensure the height is properly
      // calculated after content-visibility, image decode, and layout updates.
      requestAnimationFrame(() => {
        if (this.#state.activeItem !== item || !submenu.isConnected) return;

        const submenuHeight = getSubmenuHeight(submenu);
        if (submenuHeight <= 0) return;

        this.headerComponent?.style.setProperty(
          '--submenu-height',
          `${submenuHeight}px`
        );
        this.#setFullOpenHeaderHeight(submenuHeight);
      });
    });
  }

  #updateActiveSubmenuHeight() {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const submenu = findSubmenu(activeItem);
    if (!submenu) return;

    this.#queueSubmenuHeightUpdate(submenu, activeItem);
  }

  #cleanupSubmenuObservers() {
    this.#cleanupMutationObserver();
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  #cleanupMutationObserver() {
    clearTimeout(this.#submenuMutationObserverTimer);
    this.#submenuMutationObserverTimer = undefined;
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Measure the visible height needed by a submenu.
 *
 * @param {HTMLElement | null} submenu
 * @returns {number}
 */
function getSubmenuHeight(submenu) {
  if (!submenu) return 0;

  const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
  if (submenuInner instanceof HTMLElement) {
    return Math.max(submenu.offsetHeight, submenuInner.offsetHeight, submenuInner.scrollHeight);
  }

  return submenu.offsetHeight || 0;
}

/**
 * Find the closest menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(
      element.parentElement?.querySelector('[slot="overflow"]')
    );
  }

  return element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector('[ref="submenu[]"]');

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}
